import { takeEvery, call, select, all, put, delay } from 'redux-saga/effects';
import { requestApi, isNetworkError } from "../../request-api"
import * as actions from './actions';
import * as router from '../router';
import { show as showSnackBar, hide as hideSnackbar } from '../../components/kerika-snackbar';
import i18next from '@dw/i18next-esm';
import { store } from '../../store';
import { ReduxUtils } from '@dw/pwa-helpers/redux-utils';
import get from 'lodash-es/get';
import head from 'lodash-es/head';
import last from 'lodash-es/last';
import filter from 'lodash-es/filter';
import map from 'lodash-es/map';
import sortBy from 'lodash-es/sortBy';
import flatMap from 'lodash-es/flatMap';
import * as board from '../board';
import * as columnsSelectors from '../columns/selectors.js';
import { clipboard } from '../../components/clip-board';
import { htmlToMarkdown } from '../../utils/html-to-markdown.js';
import { toEditorValue } from '../references/utils.js';
import { formatCard, escapeLinkText } from '../../utils/card-markdown-formatter.js';
import { getUserName } from '../../components/utils';
import { marked } from 'marked';
import forEach from 'lodash-es/forEach';
import every from 'lodash-es/every';
import isEmpty from 'lodash-es/isEmpty';
import keys from 'lodash-es/keys';
import find from 'lodash-es/find';
import cloneDeep from 'lodash-es/cloneDeep';
import omitBy from 'lodash-es/omitBy';
import { sortBoardCards } from './sort-cards';
import * as views from '../views';
import * as analytics from '../../analytics/analytics.js';
import * as clipboardModule from '../clipboard';
import firestoreRedux from '@dreamworld/firestore-redux';
import entityIdProvider from '../../entity-id-provider';
import * as knownFeatures from '../known-features';
import * as auth from '../auth';
import * as utils from '../../utils.js';
import * as selectors from './selectors';
import * as lastMoveCards from '../last-move-cards';
import isEqual from 'lodash-es/isEqual';

/**
 * Watch router change for AI assistance dialog.
 * Dispatch AI_ASSISTANCE_DIALOG_OPENED, AI_ASSISTANCE_DIALOG_CLOSED actions based on router state.
 */
function* watchRouter() {
  //If page is already opened, check once.
  yield call(routeChangeHandler);
  yield takeEvery(router.actions.UPDATE_ROUTER, routeChangeHandler);
}

let previousDialog;
function* routeChangeHandler() {
  const state = yield select();
  const currentDialog = get(state, 'router.page.params.action') || get(state, 'router.dialog.params.action');
  const cardId = router.selectors.cardId(state);
  if (currentDialog === 'ai-assistant') {
    if (!previousDialog) {
      yield put({ type: actions.AI_ASSISTANCE_DIALOG_OPENED, cardId });
    }
  } else if (previousDialog === 'ai-assistant' && !currentDialog) {
    yield put({ type: actions.AI_ASSISTANCE_DIALOG_CLOSED, cardId });
  }
  previousDialog = currentDialog;
}

/**
 * Loads cards by board.
 * @param {*} param0
 *  @property {String} requesterId Requester Id for firestore query.
 *  @property {String} boardId Board Id of which cards will be loaded.
 */
function* loadBoardCards({boardId}) {
  if (!boardId) {
    throw 'loadBoardCards: boardId is required';
  }

  firestoreRedux.query('cards', { id: `cards_${boardId}`, where: [['boardId', '==', boardId]] });
}

/**
 * Loads cards by column.
 * @param {*} param0
 *  @property {String} requesterId Requester Id for firestore query.
 *  @property {String} columnId Column Id of which cards will be loaded.
 */
function* loadCardsByColumnType({requesterId, boardId, columnType, limit}) {
  if (!requesterId || !columnType || (columnType !== 'DONE' && columnType !== 'TRASH')) {
    throw 'loadCardsByColumnType: requesterId & columnType are required';
  }

  const state = yield select();
  const cardQueryId = columnType === 'DONE' ? `done_cards_${boardId}`: columnType === 'TRASH' ? `trash_cards_${boardId}`: `cards_${boardId}`;
  const queryStatus = firestoreRedux.selectors.queryStatus(state, cardQueryId);
  const cardSummaryQueryId = columnType === 'DONE' ? `done_cards_summary_${boardId}`: columnType === 'TRASH' ? `trash_cards_summary_${boardId}`: `cards_summary_${boardId}`;
  if(queryStatus === 'PENDING') {
    return;
  }
  firestoreRedux.cancelQuery(cardQueryId);
  firestoreRedux.cancelQuery(cardSummaryQueryId);
  firestoreRedux.query('cards', { id: cardQueryId, where: [['boardId', '==', boardId], ['columnType', '==', columnType]], limit: limit || 100, orderBy: [['columnOrder']], requesterId });
  firestoreRedux.query('cards-summary', { id: cardSummaryQueryId, where: [['boardId', '==', boardId], ['columnType', '==', columnType]], limit: limit || 100, orderBy: [['columnOrder']], requesterId });
}

/**
 * Cancels cards by column query by it's requester id.
 * @param {*} param0
 *  @property {String} requesterId Firestore requester ID.
 */
function* disconnectCardsByColumnType({ requesterId }) {
  firestoreRedux.cancelQueryByRequester(requesterId);
}

/**
 * Sends request to create new card.
 * @param {*} param0
 *  @property {String} title Card title.
 *  @property {String} boardId Board ID in which card will be added.
 *  @property {String} columnId Column ID in which card will be added.
 */
function* addNewCard({ title, boardId, columnId }) {

  analytics.logBoardEvent('card added');
  if(!title || !columnId || !boardId) {
    throw new Error('addNewCard: Mandatory paramerted are not defined');
  }

  const state = yield select();
  const id = entityIdProvider.getId();
  const cards = board.selectors.cardsByColumn(state, columnId) || [];
  const lastCard = last(cards);
  const columnOrder = (lastCard) ? lastCard.columnOrder + 1000 : 0;
  //Save local data on redux state to display card instant
  const body = {
    id: `crd_${id}`,
    title,
    boardId,
    columnId,
    columnOrder,
    status: 'NORMAL',
    priority: 'NORMAL'
  };

  firestoreRedux.save(`boards/${boardId}/cards`, {...body, local: true}, { localWrite: true, queryId: `cards_${boardId}` });

  store.dispatch(knownFeatures.actions.markAsKnown('ADD_NEW_CARD'));
  requestApi('/card/cards', {method: 'POST', body});
}

/**
 * Moves card to Done column.
 * @param {Object} action action payload. e.g {cardId}
 */
function* moveToDone({ cardId }) {
  yield call(moveCardsToDone, { selectedCards: [cardId] });
}

/**
 * Moves card to Trash column.
 * @param {Object} action action payload. e.g {cardId}
 */
function* moveToTrash({ cardId }) {
  yield call(moveCardsToTrash, { selectedCards: [cardId] });
}


/**
 * Restore provided or selected cards from TRASH column.
 * @param {Arary} cardIds List of card Ids.
 * @param {Boolean} doNotUndo when `true` doesn't show UNDO in toast.
 */
function* restore({ cardIds, doNotUndo }) {
  const state = yield select();
  const selectedColumnId = board.selectors.selectedColumn(state);
  const boardId = router.selectors.pageBoardId(state);
  const trashColumnId = columnsSelectors.currentBoardTrashColumnId(state);
  if (selectedColumnId == trashColumnId && isEmpty(cardIds)) { // When cardIds not provided, gets selected cards.
    cardIds = board.selectors.selectedCards(state);
  }

  if (isEmpty(cardIds)) {
    console.warn('No cardIds provided or selected for restore.');
    return;
  }

  //Cards with it's order. It's used to UNDO restore.
  let currentCardsDocs = [];
  forEach(cardIds, (id) => {
    const doc = firestoreRedux.selectors.doc(state, 'cards', id);
    currentCardsDocs.push(doc)
  })

  try {
    yield put(board.actions.clearSelection());
    const body = { cardIds };
    const response = yield call(requestApi, `/card/cards/restore`, { method: 'POST', body, excludeErrors: [400, 403, 409] })
    const restoredCardsCount = keys(response.restored).length;
    const skipRestoreCards = response && response.skipRestore || [];

    //Remove skip restore cards.
    currentCardsDocs = omitBy(currentCardsDocs, (card)=>{
      return card && card.id && skipRestoreCards.includes(card.id);
    });

    // Set toast message.
    let message;
    if (isEmpty(response.skipRestore)) {
      if (cardIds.length > 1) {
        message = i18next.t('board-card:restoreToast.multiCardRestored');
      } else {
        message = i18next.t('board-card:restoreToast.singleCardRestored', { columnName: get(response, `restored.${cardIds[0]}.columnName`) });
      }
    } else {
      if (cardIds.length > 1) {
        message = i18next.t('board-card:restoreToast.multiCardRestoredFailed', {restoredCardsCount});
      } else {
        message = i18next.t('board-card:restoreToast.singleCardRestoreFailed');
      }
    }

    const actionAt = Date.now();
    const actionName = 'RESTORE_CARDS';
    const actionId = `${actionName}-${actionAt}`;
    const actionButton = doNotUndo || !restoredCardsCount ? undefined : { caption: i18next.t('buttons.undo'), callback: () => { store.dispatch(actions.undoRestore(currentCardsDocs)) } };
    showSnackBar({ id: actionId, message, actionButton });
    if(!isEmpty(currentCardsDocs)){
      yield put(lastMoveCards.actions.update({boardId: boardId, columnId: trashColumnId, cards: currentCardsDocs, actionBy: auth.selectors.currentUserId(state), actionAt: actionAt, actionName}));
    }
  } catch (err) {
    if(isNetworkError(err)) {
      return;
    }

    const code = err && err.code || 'UNKNOWN';
    if(code === 'CARD_NOT_IN_DONE_OR_TRASH') {
      console.error('Failed to restore cards: Card is not in DONE or TRASH column', JSON.stringify({ cardIds, boardId, trashColumnId, selectedColumnId }));
      return;
    }

    console.error('Failed to restore cards', err);
  }
}

/**
 * Restores all cards from TRASH column.
 */
function* restoreAll() {
  const state = yield select();
  const trashColumnId = columnsSelectors.currentBoardTrashColumnId(state);
  const cardsByColumn = board.selectors.cardsByColumn(state, trashColumnId) || [];
  const cardIds = map(cardsByColumn, 'id');
  yield call(restore, { cardIds });
}

/**
 * Undo the restore action.
 * @param {Object} cards List of cards to be restored. e.g. [{id, title, columnOrder, createdAt, columnType}, ...]
 */
function* undoRestore({ cards }) {
  if (isEmpty(cards)) {
    console.warn('Undo restore: cards is not provided.');
    return;
  }
  const state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  const columnId = columnsSelectors.currentBoardTrashColumnId(state);
  firestoreRedux.save(`boards/${boardId}/cards`, cards, { localWrite: true });
  const cardIdWiseOrder = {};
  forEach(cards, (card) => {
    cardIdWiseOrder[card.id] = card.columnOrder
  })

  try {
    yield call(moveCardsApi, { boardId, columnId, cardIdWiseOrder });
  } catch (error) {
    console.error(`Failed to undo restored cards`, error);
  }
}

let lastMoveCardIds = [];
let lastMoveCardIdsTimer;
async function moveCardsApi({ boardId, columnId, cardIdWiseOrder }) {
  try {

    const cardIds = keys(cardIdWiseOrder);
    if(isEqual(lastMoveCardIds, cardIds)) {
      console.debug('Last moved card ids are same.', cardIds);
    } else {
      let sameCard = false;
      let sameCardId;
      forEach(cardIds, (id) => {
        if(lastMoveCardIds && lastMoveCardIds.includes(id)) {
          sameCard = true;
          sameCardId = id;
          return false;
        }
      });

      if(sameCard) {
        console.debug('Last moved card ids are same.', sameCardId, cardIds);
      }
    }
    
    lastMoveCardIds = cardIds;
    //Reset last moved card ids after 2 minutes.
    lastMoveCardIdsTimer && clearTimeout(lastMoveCardIdsTimer);
    lastMoveCardIdsTimer = setTimeout(async () => {
      lastMoveCardIds = [];
    }, 2 * 60 * 1000);
    return requestApi(`/card/cards/move-with-order`, { excludeErrors: [403, 404], method: 'POST', body: { boardId, columnId, cardIdWiseOrder } });
  } catch (error) {
    console.error(`Failed to move cards`, error);
  }
}

/**
 * Copy link of the given or selected card.
 * @param {*} param0 {cardId}. Card Id is optional. When it's not given, it get link of selected card.
 */
function* getLink({ cardId }) {
  const state = yield select();

  // If cardId not provided, gets selected card's id.
  if (!cardId) {
    const pageName = router.selectors.pageName(state);
    const selectedCards = pageName === 'BOARD' ? board.selectors.selectedCards(state) : views.selectors.selectedCards(state);
    cardId = get(selectedCards, '0');
  }

  const url = selectors.cardUrl(state, cardId);
  if (!url) {
    console.error('Get Link: Card Id is not provided or no selected card found.', { cardId });
    return;
  }

  clipboard.copy(url);
  showSnackBar({ message: i18next.t('card-details:copyCardUrl.successMessage') });
}

/**
 * @returns {Array} Ids of the column's selected cards in on-screen order, or of all its cards when none is selected.
 */
function columnCopyCardIds(state, columnId) {
  const isBoardPage = router.selectors.pageName(state) === 'BOARD';
  const selectedCardIds = (isBoardPage ? board.selectors.selectedCards(state) : views.selectors.selectedCards(state)) || [];
  const columnCardIds = isBoardPage
    ? map(board.selectors.cardsByColumn(state, columnId), 'id')
    : map(get(views.selectors.cardsList({ state, columnId }), 'cards'), 'cardId');
  const selectedInColumn = filter(columnCardIds, (cardId) => selectedCardIds.includes(cardId));
  return isEmpty(selectedInColumn) ? columnCardIds : selectedInColumn;
}

/**
 * Copies the column's selected or all cards as a numbered Markdown list of linked titles.
 */
function* getLinks({ columnId }) {
  const state = yield select();
  const lines = map(columnCopyCardIds(state, columnId), (cardId, index) =>
    `${index + 1}. [${escapeLinkText(get(selectors.attrs(state, cardId), 'title'))}](${selectors.cardUrl(state, cardId)})`
  );
  if (isEmpty(lines)) {
    return;
  }

  const md = lines.join('\n');
  try {
    yield startRichClipboardWrite(Promise.resolve(md));
  } catch (err) {
    console.warn('Get links: rich clipboard write failed, falling back to plain text.', err);
    clipboard.copy(md);
  }
  showSnackBar({ message: i18next.t('board-card:toast.getLinks.success', { count: lines.length }) });
}

/**
 * Extracts the user ids referenced by `@usr_xxx` mentions in a chat message.
 * @param {String} text Comment HTML / plain text.
 * @returns {Array} User ids (e.g. `['usr_abc', ...]`).
 */
function extractMentionedUserIds(text) {
  const matches = (text || '').match(/@usr_[A-Za-z0-9]+/gi) || [];
  return matches.map((token) => token.slice(1)); // strip leading '@' → `usr_xxx`
}

/**
 * Replaces `@usr_xxx` mention markers with `@name`. Runs on the raw HTML before
 * the Markdown conversion, since turndown escapes the `_` in `@usr_xxx`.
 * @param {String} text Comment HTML / plain text.
 * @param {Object} userNamesById Map of user id → resolved user name.
 * @returns {String} Text with mentions resolved to names.
 */
function resolveMentions(text, userNamesById) {
  return (text || '').replace(/@usr_[A-Za-z0-9]+/gi, (token) => {
    const id = token.slice(1); // strip leading '@' → `usr_xxx`
    return `@${userNamesById[id] || id}`;
  });
}

/**
 * Resolves the id of the currently selected card, reading from the board or the
 * views page depending on which one is active.
 * @param {Object} state Redux state.
 * @returns {String|undefined} Selected card id.
 */
function getSelectedCardId(state) {
  const pageName = router.selectors.pageName(state);
  const selectedCards = pageName === 'BOARD' ? board.selectors.selectedCards(state) : views.selectors.selectedCards(state);
  return get(selectedCards, '0');
}

/**
 * Re-queries the card and its related docs (board-tags, sub-tasks, attachments &
 * comments) fresh & in parallel so the copied content reflects the latest data.
 * @param {String} boardId Board id.
 * @param {String} cardId Card id.
 * @returns {Object} `{ card, tasks, attachments, comments }`.
 */
function* fetchCardData(boardId, cardId) {
  // `cards` is a subcollection of `boards`, so the full subcollection path is used.
  const cardQuery = firestoreRedux.getDocById(`boards/${boardId}/cards`, cardId, { once: true });
  const boardTagsQuery = firestoreRedux.query('board-tags', { where: [['boardId', '==', boardId]], once: true });
  const tasksQuery = firestoreRedux.query('tasks', { where: [['boardId', '==', boardId], ['cardId', '==', cardId]], once: true });
  const attachmentsQuery = firestoreRedux.query('card-attachments', { where: [['boardId', '==', boardId], ['cardId', '==', cardId]], once: true });
  // Mirror the app's existing chat query criteria (`createdAt desc`) so it reuses
  // the already-provisioned composite index; comments are re-sorted oldest →
  // newest client-side by the caller.
  const commentsQuery = firestoreRedux.query('card-chat-messages', { where: [['boardId', '==', boardId], ['cardId', '==', cardId]], orderBy: [['createdAt', 'desc']], once: true });
  // board-tags result is intentionally discarded; the query only needs to
  // populate state so `cardAssignedTags` can resolve tag names later.
  const [card, , tasks = [], attachments = [], comments = []] = yield all([cardQuery.result, boardTagsQuery.result, tasksQuery.result, attachmentsQuery.result, commentsQuery.result]);
  return { card, tasks, attachments, comments };
}

/**
 * Fetches any not-yet-loaded `users` docs for the given ids, then returns a map
 * of user id → resolved display name from the freshest state.
 * @param {Array} userIds Referenced user ids.
 * @returns {Object} Map of user id → resolved user name.
 */
function* resolveUserNames(userIds) {
  let state = yield select();
  const missingUserQueries = filter(userIds, (userId) => isEmpty(firestoreRedux.selectors.doc(state, 'users', userId)))
    .map((userId) => firestoreRedux.getDocById('users', userId, { once: true }));
  if (!isEmpty(missingUserQueries)) {
    yield all(missingUserQueries.map((query) => query.result));
    state = yield select();
  }

  const users = firestoreRedux.selectors.collection(state, 'users') || {};
  const userNamesById = {};
  forEach(userIds, (userId) => { userNamesById[userId] = getUserName(get(users, userId)); });
  return userNamesById;
}

/**
 * Builds the Markdown string for the card: collects referenced users, resolves
 * their names & the card's tags, converts the description & comment bodies from
 * HTML → Markdown, then delegates to `formatCard`.
 * @param {Object} param0 `{ card, boardId, cardId, tasks, attachments, comments }`.
 * @returns {String} Markdown source for the card.
 */
function* buildCardMarkdown({ card, boardId, cardId, tasks, attachments, comments }) {
  // Visible comments only (deleted ones are skipped), sorted oldest → newest.
  const visibleComments = sortBy(filter(comments, (comment) => !comment.deleted), ['createdAt']);

  // Collect every referenced user id (card + sub-task assignees, comment authors
  // & @-mentions) so their names can be resolved in one pass.
  const cardAssigneeIds = get(card, 'assignments') || [];
  const referencedUserIds = [...new Set([
    ...cardAssigneeIds,
    ...flatMap(tasks, (task) => get(task, 'assignments') || []),
    ...map(visibleComments, 'createdBy'),
    ...flatMap(visibleComments, (comment) => (comment.messageHtml != null ? [] : extractMentionedUserIds(comment.message))),
  ].filter(Boolean))];
  const userNamesById = yield call(resolveUserNames, referencedUserIds);

  // Resolve assignee & tag names from the freshest state.
  const state = yield select();
  const assigneeNames = map(cardAssigneeIds, (userId) => userNamesById[userId]).filter(Boolean);
  const tagNames = map(selectors.cardAssignedTags(state, cardId, boardId), 'name').filter(Boolean);

  // Convert the description, each comment body & each sub-task title from
  // HTML → Markdown in parallel. An old comment is plain text, converted as the chat
  // shows it, after its mentions are resolved on the raw text. Sub-task titles prefer the rich
  // `title2` and fall back to `title`.
  const [descriptionMd, ...rest] = yield all([
    call(htmlToMarkdown, toEditorValue({ descriptionHtml: get(card, 'descriptionHtml'), description: get(card, 'description') }, { isOurUrl: router.actions.isAppUrl, oldHtml: get(card, 'description2') })),
    ...map(visibleComments, (comment) =>
      call(htmlToMarkdown, toEditorValue({ descriptionHtml: comment.messageHtml, description: resolveMentions(comment.message, userNamesById) }, { isOurUrl: router.actions.isAppUrl, oldHtml: comment.htmlMessage }))
    ),
    ...map(tasks, (task) => call(htmlToMarkdown, get(task, 'title2') || get(task, 'title'))),
  ]);
  const commentBodies = rest.slice(0, visibleComments.length);
  const taskTitlesMd = rest.slice(visibleComments.length);
  const formattedComments = map(visibleComments, (comment, i) => ({
    createdBy: comment.createdBy,
    createdAt: comment.createdAt,
    lastUpdatedAt: comment.lastUpdatedAt,
    bodyMd: commentBodies[i],
  }));
  const tasksWithTitleMd = map(tasks, (task, i) => ({ ...task, titleMd: taskTitlesMd[i] }));

  return formatCard({
    title: get(card, 'title'),
    descriptionMd,
    status: get(card, 'status'),
    priority: get(card, 'priority'),
    dueDate: get(card, 'dueDate'),
    assigneeNames,
    tagNames,
    columnType: get(card, 'columnType'),
    url: selectors.cardUrl(state, cardId),
    refNo: get(card, 'refNo'),
    showCardRefNo: board.selectors.autoNumberEnabled(state, boardId),
    tasks: tasksWithTitleMd,
    userNamesById,
    attachments,
    state,
    comments: formattedComments,
  });
}

/**
 * Starts writing the Markdown to the clipboard as both `text/plain` (raw Markdown) and `text/html` (rendered), so
 * plain-text targets get Markdown source while rich-text targets (Docs, Slack, Notion) get formatted output.
 * Called in the click, before the card loads: Chrome writes to the clipboard only while the page has focus, which
 * may be gone once the card has loaded. The clipboard waits for `markdown` to fill both formats.
 * @param {Promise<String>} markdown The Markdown, once built.
 * @returns {Promise} The write; rejects when the rich write is refused or not supported.
 */
const startRichClipboardWrite = (markdown) => {
  try {
    const write = navigator.clipboard.write([new ClipboardItem({
      'text/plain': markdown.then((md) => new Blob([md], { type: 'text/plain' })),
      'text/html': markdown.then((md) => new Blob([marked(md, { breaks: true })], { type: 'text/html' })),
    })]);
    // Handled where it is awaited; this only keeps a write abandoned by a failed load from being reported.
    write.catch(() => {});
    return write;
  } catch (err) {
    return Promise.reject(err);
  }
};

/**
 * Waits for the rich write and shows its outcome, falling back to a plain-text copy when it fails (non-secure
 * context, older browser, or permission denied). The given toast is reused for the outcome message so only the
 * text changes — avoiding the flicker of dismissing & re-showing a toast.
 * @param {Promise} richWrite The write from `startRichClipboardWrite`.
 * @param {String} md Markdown source.
 * @param {String} toastId In-progress snackbar id to update with the outcome.
 * @param {String} successMessage Shown when the rich write succeeds.
 */
function* finishClipboardWrite(richWrite, md, toastId, successMessage) {
  try {
    yield richWrite;
    showSnackBar({ id: toastId, message: successMessage });
  } catch (err) {
    // Rich (dual-format) clipboard write failed — fall back to a plain-text copy.
    // A failure here propagates to the caller, which surfaces the error toast.
    console.warn('Copy as Markdown: rich clipboard write failed, falling back to plain text.', err);
    clipboard.copy(md);
    showSnackBar({ id: toastId, message: i18next.t('board-card:toast.copyAsMarkdown.failure') });
  }
}

/**
 * Copies the given or selected card to the clipboard as Markdown.
 * The card is always re-queried fresh so the copied content is up to date.
 * @param {*} param0 {cardId}. Card Id is optional. When it's not given, copies the selected card.
 */
function* copyAsMarkdown({ cardId }) {
  const state = yield select();

  // If cardId not provided, gets selected card's id.
  cardId = cardId || getSelectedCardId(state);
  if (!cardId) {
    console.error('Copy as Markdown: Card Id is not provided or no selected card found.');
    return;
  }

  const boardId = get(firestoreRedux.selectors.doc(state, 'cards', cardId), 'boardId');
  if (!boardId) {
    console.error('Copy as Markdown: Board Id could not be resolved for card.', { cardId });
    return;
  }

  // Show an in-progress snackbar immediately so the user knows the click
  // registered; it stays (timeout 0) but is dismissable, and is replaced (by
  // this stable id) once the copy finishes.
  const inProgressToastId = `copy-as-md-${cardId}`;
  showSnackBar({
    id: inProgressToastId,
    message: i18next.t('board-card:toast.copyAsMarkdown.inProgress'),
    timeout: 0,
    hideDismissBtn: false,
  });

  // Started before the first await, while the click still holds the page's focus.
  const { promise: markdown, resolve: resolveMarkdown, reject: rejectMarkdown } = deferredPromise();
  const richWrite = startRichClipboardWrite(markdown);

  // The whole load → format → copy flow is wrapped so any failure (network,
  // permission, missing doc, conversion error) replaces the in-progress toast
  // with an error toast instead of leaving it stuck on screen.
  try {
    const { card, tasks, attachments, comments } = yield call(fetchCardData, boardId, cardId);
    if (isEmpty(card)) {
      console.error('Copy as Markdown: Card not found.', { cardId, boardId });
      rejectMarkdown(new Error('Card not found'));
      showSnackBar({ id: inProgressToastId, message: i18next.t('board-card:toast.copyAsMarkdown.error'), type: 'ERROR' });
      return;
    }

    const md = yield call(buildCardMarkdown, { card, boardId, cardId, tasks, attachments, comments });
    resolveMarkdown(md);
    yield call(finishClipboardWrite, richWrite, md, inProgressToastId, i18next.t('board-card:toast.copyAsMarkdown.success'));
  } catch (err) {
    rejectMarkdown(err);
    // Any failure while loading, formatting or copying the card — surface an error
    // toast so the in-progress toast is replaced rather than left lingering.
    console.error('Copy as Markdown: failed to copy card as Markdown.', { cardId, boardId }, err);
    showSnackBar({ id: inProgressToastId, message: i18next.t('board-card:toast.copyAsMarkdown.error'), type: 'ERROR' });
  }
}

function deferredPromise() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
  return { promise, resolve, reject };
}

/**
 * Loads a card fresh and builds its Markdown; throws when the card is not found.
 */
function* loadCardMarkdown(cardId) {
  const boardId = selectors.cardBoardId(yield select(), cardId);
  const { card, tasks, attachments, comments } = yield call(fetchCardData, boardId, cardId);
  if (isEmpty(card)) {
    throw new Error(`Card not found: ${cardId}`);
  }
  return yield call(buildCardMarkdown, { card, boardId, cardId, tasks, attachments, comments });
}

/**
 * Copies the column's selected or all cards as Markdown, one section each, separated by `---`.
 * Copies nothing when any card fails to load.
 */
function* copyCardsAsMarkdown({ columnId }) {
  const state = yield select();
  const cardIds = columnCopyCardIds(state, columnId);
  if (isEmpty(cardIds)) {
    return;
  }

  const count = cardIds.length;
  const toastId = 'copy-as-md-multiple';
  showSnackBar({
    id: toastId,
    message: i18next.t('board-card:toast.copyCardsAsMarkdown.inProgress', { count }),
    timeout: 0,
    hideDismissBtn: false,
  });

  // Started before the first await, while the click still holds the page's focus.
  const { promise: markdown, resolve: resolveMarkdown, reject: rejectMarkdown } = deferredPromise();
  const richWrite = startRichClipboardWrite(markdown);

  try {
    const cardMarkdowns = yield all(map(cardIds, (cardId) => call(loadCardMarkdown, cardId)));
    const md = cardMarkdowns.join('\n\n---\n\n');
    resolveMarkdown(md);
    yield call(finishClipboardWrite, richWrite, md, toastId, i18next.t('board-card:toast.copyCardsAsMarkdown.success', { count }));
  } catch (err) {
    rejectMarkdown(err);
    console.error('Copy cards as Markdown: failed.', { columnId, cardIds }, err);
    showSnackBar({ id: toastId, message: i18next.t('board-card:toast.copyCardsAsMarkdown.error', { count }), type: 'ERROR' });
  }
}

/**
 * Resets clipboard.
 * Requests server to copy cards.
 * On successfull copy of all cards, selects copied cards & dispatches `COPY_SUCCESS` action so View element can perform appropriate action.
 * @param {Object} action action payload
 */
function* copyCards(action) {
  const state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  const columnId = action.columnId;
  const copiedCards = clipboardModule.selectors.copiedCards(state);

  if (isEmpty(copiedCards)) {
    return;
  }

  const sourceBoard = get(state, `clipboard.data.boardId`);
  const sourceColumn = get(state, `clipboard.data.columnId`);
  if (!sourceColumn || !sourceBoard) {
    console.warn('Source column or board not found in clipboard data.');
    return;
  }

  const cardsQuery = firestoreRedux.query('cards', { where: [['boardId', '==', sourceBoard], ['columnId', '==', sourceColumn]], orderBy: [['columnOrder']], once: true });
  yield cardsQuery.waitTillResultAvailableInState();
  let cardIds = [];
  forEach(copiedCards, (cardId) => {
    const doc = firestoreRedux.selectors.doc(state, 'cards', cardId);
    if (doc && doc.columnId === sourceColumn) {
      cardIds.push(cardId);
    }
  });

  const toastId = Date.now();
  if (isEmpty(cardIds)) {
    yield put(clipboardModule.actions.reset());
    showSnackBar({ id: toastId, message: 'Copied cards moved to another column or deleted permanently' });
    return;
  }

  const body = { cardIds, boardId, columnId };
  const cardCounts = cardIds.length;
  try {
    yield put(clipboardModule.actions.reset());
    showSnackBar({ id: toastId, message: i18next.t('board-card:copy.toast.inProgress', {count: cardCounts}) });
    const docs = yield call(requestApi, `/card/cards/copy`, {
      excludeErrors: [400],
      method: 'POST',
      body
    });
    cardIds = map(docs, 'id');
    yield call(onCopySuccess, cardIds);
    yield put({type: board.actions.CARD_SELECTION_UPDATE, cardIds, columnId });
    yield put({ type: actions.COPY_SUCCESS, columnId });
    toastId && hideSnackbar(toastId);
  } catch (err) {
    if (!err || !err.code || err.status == 504) {
      return;
    }

    if (err.code === 'NOT_FROM_SAME_COLUMN') {
      showSnackBar({ message: i18next.t('board-card:copy.toast.notFromSameColumn'), type: 'ERROR' });
      return;
    }

    if (err.code === 'BOARD_NOT_FROM_SAME_ACCOUNT') {
      showSnackBar({ message: i18next.t('board-card:copy.toast.notAllowed'), type: 'ERROR' });
      return;
    }

    console.error(`Failed to copy selected cards:`, err);
    toastId && hideSnackbar(toastId);
  }
}

const CARDS_COPY_TIMEOUT = 120000; // timeout for cards copy operation.
/**
 * @returns Promise resolved when all cards are copied successfully.
 * If after 2 minutes, promise is not resolved, reject it.
 */
const onCopySuccess = (cardIds) => {
  const promise =  new Promise((resolve, reject) => {
    const unsubscribe = ReduxUtils.subscribe(store, `firestore.docs.cards`, () => {
      const cards = firestoreRedux.selectors.collection(store.getState(), 'cards');
      const copied = every(cardIds, (id) => {
        return cards[id] ? true : false;
      });
      if (copied) {
        resolve();
        unsubscribe && unsubscribe();
      }
    });

    setTimeout(() => {
      if (!promise.isFulfilled) {
        reject('Copied cards are not reflected in firebase realtime database yet.');
        unsubscribe && unsubscribe();
      }
    }, CARDS_COPY_TIMEOUT);
  });
  return promise
}

/**
 * Deletes provided card or selected or all cards from the provided column.
 */
function* permanentDelete(action) {
  const state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  let currentDocs = [];
  try {
    let cardIds = action.cardIds;
    const columnId = action.columnId;
    if (isEmpty(cardIds)) {
      if (!columnId) {
        return;
      }

      cardIds = board.selectors.selectedCards(state); //Selected cards.

      if (isEmpty(cardIds)) {
        const cards = board.selectors.cardsByColumn(state, columnId) || []; //All the cards of column.
        cardIds = map(cards, 'id');
      }
    }

    if (isEmpty(cardIds)) {
      return;
    }

    forEach(cardIds, (id) => {
      const doc = firestoreRedux.selectors.doc(state, 'cards', id);
      currentDocs.push(doc);
    })

    firestoreRedux.delete(`boards/${boardId}/cards`, cardIds, { localWrite: true });
    const body = { cardIds };
    yield call(requestApi, `/card/cards/delete`, { method: 'POST', body, excludeErrors: [404] });
  } catch (error) {
    if(isNetworkError(error) || error.code === 'CARD_NOT_FOUND' || error.code === 'USER_DELETION_IN_PROGRESS' || error.status == 504) {
      return;
    }
    firestoreRedux.save(`boards/${boardId}/cards`, currentDocs, { localWrite: true });
    console.error('Failed to delete the card permanently.', error);
  }
}

/**
 * Sorts cards within column by provided `sortType` option & `selectedCards`.
 * Request server to update order of cards.
 * @param {Object} action action payload
 */
function* sortCards({sortBy, columnId}) {
  const state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  const cardIdWiseOrder = sortBoardCards(sortBy, columnId);

  if (isEmpty(cardIdWiseOrder)) {
    return;
  }

  let currentCards = [];
  let newCards = [];
  forEach(cardIdWiseOrder, (order, cardId) => {
    const doc = firestoreRedux.selectors.doc(state, 'cards', cardId);
    if (!isEmpty(doc)) {
      currentCards.push(doc);
      const newDoc = cloneDeep(doc);
      newDoc.columnOrder = order;
      newCards.push(newDoc);
    }
  })

  if (isEmpty(currentCards) || isEmpty(newCards)) {
    return;
  }

  try {
    firestoreRedux.save(`boards/${boardId}/cards`, newCards, { localWrite: true });
    yield call(moveCardsApi, { boardId, columnId, cardIdWiseOrder });
    const actionAt = Date.now();
    const actionName = 'SORT';
    const actionId = `${actionName}-${actionAt}`;
    showSnackBar({
      id: actionId,
      message: i18next.t(`board-card:sortToast.${sortBy}`, { count: keys(cardIdWiseOrder).length }),
      actionButton: {
        caption: i18next.t('buttons.undo'),
        callback: () => { store.dispatch(actions.undoSort(currentCards, actionId)) }
      }
    });

    yield put(lastMoveCards.actions.update({boardId, columnId, cards: currentCards, actionBy: auth.selectors.currentUserId(state), actionAt, actionName}));
  } catch (error) {
    firestoreRedux.save(`boards/${boardId}/cards`, currentCards, { localWrite: true });
    console.error(`Failed to sort cards of column: ${columnId}`, error);
  }
}

/**
 * Undo previously performed sort actions.
 * @param {Object} action action payload. e.g {cards}
 * @returns
 */
function* undoSort({cards, actionId}) {
  yield call(undoMove, { cards, actionId });
}

/**
 * Moves card within board before given card, When before not given, moves to last of the column.
 * @param {*} param0
 *  @property {Array} cardIds List of moved card's ids.
 *  @property {String} destColumnId Destination column Id.
 *  @property {String} before Card Id, before which cards will be moved.
 */
function* moveCardsWithinBoardBeforeCard({ cardIds, destColumnId,columnId, before }) {
  if (isEmpty(cardIds) || !destColumnId) {
    console.warn('moveCardsWithinBoardBeforeCard: Please provided mandatory fields.', { cardIds, destColumnId });
    return;
  }
  const state = yield select();
  const destBoardId = router.selectors.pageBoardId(state);

  let cards = [];
  forEach(cardIds, (id) => {
    const doc = firestoreRedux.selectors.doc(state, 'cards', id);
    if (!isEmpty(doc) && doc.boardId === destBoardId) {
      cards.push(doc);
    }
  })

  if (isEmpty(cards)) {
    showSnackBar({ message: i18next.t('board-page:dragAndDrop.message')});
    return;
  }

  const confirmResponse = yield call(moveCards, { destBoardId, destColumnId, cards, before, dragAndDrop: true });
  if(confirmResponse) {
    yield put(lastMoveCards.actions.update({boardId: destBoardId, columnId, cards, actionBy: auth.selectors.currentUserId(state), actionAt: Date.now(), actionName: 'MOVE_CARDS_WITHIN_BOARD_BEFORE_CARD'}));
  }
}

/**
 *
 * @param {*} param0
 *  @property {String} destBoardId Destination board Id
 *  @property {String} destColumnId Destination column Id.
 *  @property {Array} cards List of cards to be moved.
 *  @property {String} before before card id. If it's not provided, it considers first card as before.
 *  @property {String} dragAndDrop `true` when cards are moved through drag & drop. When it's `true` & `before` is not given, card(s) will be moved at bottom otherwise at top.
 */
function* moveCards({ destBoardId, destColumnId, cards, before, dragAndDrop }) {
  let state = yield select();
  let newCards = cloneDeep(cards);
  let openTaskCount = 0;
  forEach(cards, (card) => {
    const summary = selectors.summary(state, card.id);
    if(summary && summary.tasks && summary.tasks.open) {
      openTaskCount = openTaskCount + summary.tasks.open;
    }
  });

  //load dest column data.
  let destColumn = columnsSelectors.column(state, destColumnId);
  if(isEmpty(destColumn)) {
    const destColumnQuery = firestoreRedux.getDocById(`boards/${destBoardId}/columns`, destColumnId, {waitTillSucceed: true, once: true});
    destColumn = yield destColumnQuery.result;
  }
  const destColumnType = destColumn && destColumn.type;
  const lastMovedAt = Date.now();
  forEach(newCards, (card) => {
    card.columnId = destColumnId;
    card.previousBoardId = card.boardId;
    card.boardId = destBoardId;
    card.columnType = destColumnType || card.columnType;
    card.lastMovedAt = lastMovedAt;
  });

  const allItems = board.selectors.cardsByColumn(state, destColumnId, destBoardId) || [];
  if(destColumnType === 'DONE' || destColumnType === 'TRASH') {
    before = get(head(allItems), 'id');
  }

  before = before ? before : !dragAndDrop ? get(head(allItems), 'id') : get(last(allItems, 'id'));
  newCards = utils.getNewOrder({ allItems, movedItems: newCards, before, sortByKey: 'columnOrder', ignoreAllItemsMove: (destColumnType === 'DONE' || destColumnType === 'TRASH') });

  let cardIdWiseOrder = {};
  forEach(newCards, (item) => {
    cardIdWiseOrder[item.id] = (destColumnType === 'DONE' || destColumnType === 'TRASH') ? null: item.previousBoardId != destBoardId ? null: item.columnOrder;
  });

  if (isEmpty(cardIdWiseOrder)) {
    return;
  }

  let columnWiseCardOrders = {};
  forEach(cardIdWiseOrder, (order, cardId) => {
    const cardDoc = firestoreRedux.selectors.doc(state, 'cards', cardId);
    const columnId = cardDoc.columnId;
    columnWiseCardOrders[columnId] = columnWiseCardOrders[columnId] || {};
    columnWiseCardOrders[columnId][cardId] = order;
  })

  try {
    const destColumnQueryId = destColumnType === 'DONE' ? `done_cards_${destBoardId}`: destColumnType === 'TRASH' ? `trash_cards_${destBoardId}`: `cards_${destBoardId}`;
    firestoreRedux.save(`boards/${destBoardId}/cards`, newCards, { localWrite: true, queryId: destColumnQueryId });

    //Get confirmation when openTaskCount and destColumnType is DONE.
    let confirmResponse = true;
    if(openTaskCount && destColumnType === 'DONE') {
      const promise = yield call(openCardMoveToDoneConfirmDialog, openTaskCount, cards && cards.length || 0);
      confirmResponse = yield promise;
    }

    if(!confirmResponse) {
      firestoreRedux.save(`boards/${destBoardId}/cards`, cards, { localWrite: true, queryId: destColumnQueryId });
    } else {
      forEach(columnWiseCardOrders, async (cardIdWiseOrder) => {
        if(!isEmpty(cardIdWiseOrder)) {
          await moveCardsApi({ boardId: destBoardId, columnId: destColumnId, cardIdWiseOrder });
        }
      })
    }

    return confirmResponse;
  } catch (error) {
    firestoreRedux.save(`boards/${destBoardId}/cards`, cards, { localWrite: true });
    throw new Error(error);
  }
}

function* openCardMoveToDoneConfirmDialog(openTaskCount, cardCount) {
  try {
    let resolve, reject;
    const promise = new Promise((res, rej) => { resolve = res, reject = rej; });
    yield import('../../components/card-move-to-done-confirm-dialog.js');
    const dialog = kerikaPWA && kerikaPWA.cardMoveToDoneConfirmDialog;
    if(dialog) {
      dialog.openTaskCount = openTaskCount;
      dialog.cardCount = cardCount;
      dialog.opened = true;
      dialog.addEventListener('confirm', ()=> {resolve(true);});
      dialog.addEventListener('decline', ()=> {resolve(false);});
    } else {
      resolve(true);
    }
    return promise;
  } catch (error) {
    console.error("card > saga > openCardMoveToDoneConfirmDialog: failed due to this: ", error);
  }
}

/**
 * Undo moves
 * @param {*} param0
 *  @property {Array}  cards Cards with it's previous orders.
 */
function* undoMove({ cards, actionId }) {
  const state = yield select();
  const isUndoAvailable = lastMoveCards.selectors.isUndoAvailable(state);
  // It considers that cards are from the same column.
  const boardId = get(cards, '0.boardId');
  const columnId = get(cards, '0.columnId');
  let cardIdWiseOrder = {};
  let cardIds = [];
  forEach(cards, (card) => {
    cardIdWiseOrder[card.id] = card.columnOrder;
    cardIds.push(card.id);
  })

  try {
    if(actionId && isUndoAvailable) {
      yield put(lastMoveCards.actions.remove(actionId));
    }
    firestoreRedux.save(`boards/${boardId}/cards`, cards, { localWrite: true });
    yield call(moveCardsApi, { boardId, columnId, cardIdWiseOrder });
    yield delay(100);
    yield put({type: board.actions.CARD_SELECTION_UPDATE, cardIds, columnId });
  } catch (error) {
    console.error(`Failed to undo move cards`, error);
  }
}

/*
 * Moves selected cards to top of the column with localWrites.
 */
function* moveSelectedCardsToTop() {
  const state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  const columnId = board.selectors.selectedColumn(state);
  const selectedCards = board.selectors.selectedCards(state);
  if (!boardId || !columnId || isEmpty(selectedCards)) {
    return;
  }
  const allItems = board.selectors.cardsByColumn(state, columnId) || [];
  const currentItems = filter(allItems, (item) => selectedCards.includes(item.id));
  const withoutMovedItems = filter(allItems, (item) => !selectedCards.includes(item.id));
  const before = get(head(withoutMovedItems), 'id');

  const newItems = utils.getNewOrder({ allItems, movedItems: currentItems, before, sortByKey: 'columnOrder' });
  let cardIdWiseOrder = {};
  forEach(newItems, (item) => {
    cardIdWiseOrder[item.id] = item.columnOrder
  })

  if (isEmpty(cardIdWiseOrder)) {
    return;
  }

  try {
    firestoreRedux.save(`boards/${boardId}/cards`, newItems, { localWrite: true });
    yield call(moveCardsApi, { boardId, columnId, cardIdWiseOrder });
    yield put(lastMoveCards.actions.update({boardId, columnId, cards: currentItems, actionBy: auth.selectors.currentUserId(state), actionAt: Date.now(), actionName: 'CARDS_MOVE_TO_TOP'}));
  } catch (error) {
    firestoreRedux.save(`boards/${boardId}/cards`, currentItems, { localWrite: true });
    console.error(`Failed to move cards at top`, { boardId, columnId, cardIdWiseOrder, error });
  }
}

/**
 * Moves selected cards to bottom of the column with localWrites.
 */
 function* moveSelectedCardsToBottom() {
  const state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  const columnId = board.selectors.selectedColumn(state);
  const selectedCards = board.selectors.selectedCards(state);
  if (!boardId || !columnId || isEmpty(selectedCards)) {
    return;
  }
  const allItems = board.selectors.cardsByColumn(state, columnId) || [];
  const currentItems = filter(allItems, (item) => selectedCards.includes(item.id));

  const newItems = utils.getNewOrder({ allItems, movedItems: currentItems, sortByKey: 'columnOrder' });
  let cardIdWiseOrder = {};
  forEach(newItems, (item) => {
    cardIdWiseOrder[item.id] = item.columnOrder
  })

  if (isEmpty(cardIdWiseOrder)) {
    return;
  }

  try {
    firestoreRedux.save(`boards/${boardId}/cards`, newItems, { localWrite: true });
    yield call(moveCardsApi, { boardId, columnId, cardIdWiseOrder });
    yield put(lastMoveCards.actions.update({boardId, columnId, cards: currentItems, actionBy: auth.selectors.currentUserId(state), actionAt: Date.now(), actionName: 'CARDS_MOVE_TO_BOTTOM'}));
  } catch (error) {
    firestoreRedux.save(`boards/${boardId}/cards`, currentItems, { localWrite: true });
    console.error(`Failed to move cards at bottom`, { boardId, columnId, cardIdWiseOrder, error});
  }
}

/**
 * Moves given or selected or all cards of the column to the Done column.
 * @param {*} param0
 *  @property {String} columnId Source column Id.
 *  @property {Array} selectedCards List of card Ids. It's optional. When it's not provided, done all the card of source column.
 */
function* moveCardsToDone({columnId, selectedCards}) {
  const state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  let selectedColumn = board.selectors.selectedColumn(state);
  selectedColumn = columnId || selectedColumn;
  selectedCards = selectedCards || board.selectors.selectedCards(state);
  const cards = yield call(getCardsToBeMoved, { selectedCards, selectedColumn });

  if (isEmpty(cards)) {
    return;
  }
  const destBoardId = get(cards, '0.boardId')
  const columns = board.selectors.columns(state, destBoardId);
  const destColumnId = get(find(columns, { type: 'DONE' }), 'id');

  try {
    yield put(board.actions.clearSelection());
    const confirmResponse = yield call(moveCards, { destBoardId, destColumnId, cards });
    if(confirmResponse) {
      const actionAt = Date.now();
      const actionName = 'MOVE_TO_DONE';
      const actionId = `${actionName}-${actionAt}`;
      showSnackBar({ id: actionId, message: i18next.t('board-card:doneToast.message'), actionButton: { caption: i18next.t('buttons.undo'), callback: () => { store.dispatch(actions.undoMove({ cards, actionId })) } } });
      yield put(lastMoveCards.actions.update({boardId, columnId: selectedColumn, cards, actionBy: auth.selectors.currentUserId(state), actionAt, actionName}));
    }
  } catch (error) {
    console.error('Failed to move cards to Done', error);
  }
}

/**
 * Moves given or selected or all cards of the column to the Trash column.
 * @param {*} param0
 *  @property {String} columnId Source column Id.
 *  @property {Array} selectedCards List of card Ids. It's optional. When it's not provided, trashes all the card of source column.
 */
function* moveCardsToTrash({columnId, selectedCards}) {
  const state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  let selectedColumn = board.selectors.selectedColumn(state);
  selectedColumn = columnId || selectedColumn;
  selectedCards = selectedCards || board.selectors.selectedCards(state);
  const cards = yield call(getCardsToBeMoved, { selectedCards, selectedColumn });

  if (isEmpty(cards)) {
    return;
  }
  const destBoardId = get(cards, '0.boardId')
  const columns = board.selectors.columns(state, destBoardId);
  const destColumnId = get(find(columns, { type: 'TRASH' }), 'id');

  try {
    yield put(board.actions.clearSelection());
    const confirmResponse = yield call(moveCards, { destBoardId, destColumnId, cards });
    if(confirmResponse) {
      const actionAt = Date.now();
      const actionName = 'MOVE_TO_TRASH';
      const actionId = `${actionName}-${actionAt}`;
      showSnackBar({ id: actionId, message: i18next.t('board-card:trashToast.message'), actionButton: { caption: i18next.t('buttons.undo'), callback: () => { store.dispatch(actions.undoMove({ cards, actionId })) } } });
      yield put(lastMoveCards.actions.update({boardId, columnId: selectedColumn, cards, actionBy: auth.selectors.currentUserId(state), actionAt, actionName}));
    }
  } catch (error) {
    console.error('Failed to move cards to Trash', error);
  }
}

/**
 * Moves selected or all cards of the column to another column of the board.
 * @param {Object} action action payload e.g {type: CARDS_MOVE_TO_COLUMN, srcColumnId, destColumnId}
 */
function* moveCardsToAnotherColumn({srcColumnId, destColumnId}) {
  const state = yield select();
  const destBoardId = router.selectors.pageBoardId(state);
  const selectedColumn = srcColumnId || board.selectors.selectedColumn(state);
  const selectedCards = board.selectors.selectedCards(state);

  const cards = yield call(getCardsToBeMoved, { selectedCards, selectedColumn });
  if (isEmpty(cards)) {
    return;
  }

  try {
    yield put(board.actions.clearSelection());
    const confirmResponse = yield call(moveCards, { destBoardId, destColumnId, cards });
    if(confirmResponse) {
      const actionAt = Date.now();
      const actionName = 'CARDS_MOVE_TO_COLUMN';
      const actionId = `${actionName}-${actionAt}`;
      const columnName = columnsSelectors.column(state, destColumnId).name;
      const actionButton = { caption: i18next.t('buttons.undo'), callback: () => { store.dispatch(actions.undoMove({ cards, actionId })) } };
      showSnackBar({ id: actionId, message: i18next.t('board-card:moveToAnotherColumn.toast', {columnName}), actionButton });
      yield put(lastMoveCards.actions.update({boardId: destBoardId, columnId: selectedColumn, cards, actionBy: auth.selectors.currentUserId(state), actionAt, actionName}));
    }
  } catch (error) {
    console.error('Failed to move cards to another column', error);
  }
}

/**
 * Moves selected or all cards of the column to another column of the board.
 * @param {Object} action action payload e.g { type: CARDS_MOVE_TO_BOARD, srcColumnId, destColumnId, destBoardId }
 */
function* moveCardsToAnotherBoard({ destBoardId, srcColumnId, destColumnId }) {
  const state = yield select();
  const boardId = router.selectors.pageBoardId(state);
  const selectedColumn = srcColumnId || board.selectors.selectedColumn(state);
  const selectedCards = board.selectors.selectedCards(state);
  const cards = yield call(getCardsToBeMoved, { selectedCards, selectedColumn });
  if (isEmpty(cards)) {
    return;
  }

  try {
    const confirmResponse = yield call(moveCards, { destBoardId, destColumnId, cards });
    if(confirmResponse) {
      const accountId = router.selectors.accountId(state);
      const boardName = get(state, `writableBoards.${accountId}.favorite.${destBoardId}`) || get(state, `writableBoards.${accountId}.other.${destBoardId}`);
      const columnName = columnsSelectors.column(state, destColumnId).name;
      const actionAt = Date.now();
      const actionName = 'CARDS_MOVE_TO_BOARD';
      const actionId = `${actionName}-${actionAt}`;
      const actionButton = { caption: i18next.t('buttons.undo'), callback: () => { store.dispatch(actions.undoMove({ cards, actionId })) } };
      showSnackBar({ id: actionId, message: i18next.t('board-card:moveToAnotherBoardDialog.toast', {boardName, columnName}), actionButton });
      yield put(lastMoveCards.actions.update({boardId, columnId: selectedColumn, cards, actionBy: auth.selectors.currentUserId(state), actionAt, actionName}));
    }
  } catch (error) {
    console.error('Failed to move cards to another board', error);
  }
}

/**
 * @param {*} param0
 *  @property {Array} selectedCards List of selected card's ids.
 *  @property {String} selectedColumn Selected column Id. When selected cards provided, it's ignored, otherwise gets all cards of the selected column.
 * @returns {Array} list of card's documents.
 */
 function* getCardsToBeMoved({ selectedCards, selectedColumn }) {
  const state = yield select();
  if (isEmpty(selectedCards) && !selectedColumn) {
    return;
  }

  if (isEmpty(selectedCards) && selectedColumn) {
    const cardsByColumn = board.selectors.cardsByColumn(state, selectedColumn) || [];
    selectedCards = map(cardsByColumn, 'id');
  }

  if (isEmpty(selectedCards)) {
    return;
  }

  let cards = [];
  forEach(selectedCards, (id) => {
    const doc = firestoreRedux.selectors.doc(state, 'cards', id);
    if (doc) {
      cards.push(doc);
    }
  })

  return cards;
}

/**
 * Moves selected cards to Done column.
 * When no card is selected, moves all cards of the given column to Done column.
 */
function* moveViewsCardsToDone({ columnId }) {
  const state = yield select();
  const boardWiseCardIds = yield call(getViewsCardsToBeMoved, columnId);
  try {
    let openTaskCount = 0;
    let _cardIds = [];
    forEach(boardWiseCardIds, (cardIds, boardId) => {
      forEach(cardIds, (cardId) => {
        _cardIds.push(cardId);
        const summary = selectors.summary(state, cardId);
        if(summary && summary.tasks && summary.tasks.open) {
          openTaskCount = openTaskCount + summary.tasks.open;
        }
      });
    });

    //Get confirmation when openTaskCount and destColumnType is DONE.
    let confirmResponse = true;
    if(openTaskCount) {
      const promise = yield call(openCardMoveToDoneConfirmDialog, openTaskCount, _cardIds && _cardIds.length || 0);
      confirmResponse = yield promise;
    }

    if(!confirmResponse) {
      return;
    }

    forEach(boardWiseCardIds, (cardIds, boardId) => {
      if (isEmpty(cardIds)) {
        console.warn('moveViewsCardsToDone: No cards available for move');
        return;
      }
      const body = { boardId, cardIds, columnType: 'DONE' };
      //TODO
      requestApi(`/card/cards/move`, { excludeErrors: [400, 403, 404], method: 'POST', body });
    });
    showSnackBar({ message: i18next.t('board-card:doneToast.message'), actionButton: { caption: i18next.t('buttons.undo'), callback: () => { store.dispatch(actions.restoreViewsCards(boardWiseCardIds)) } } });
  } catch (error) {
    console.error('Views error while moving cards to Done', error);
  }
}

/**
 * Moves selected cards to Trash column.
 * When no card is selected, moves all cards of the given column to Trash column.
 */
function* moveViewsCardsToTrash({ columnId }) {
  const boardWiseCardIds = yield call(getViewsCardsToBeMoved, columnId);
  try {
    forEach(boardWiseCardIds, (cardIds, boardId) => {
      if (isEmpty(cardIds)) {
        console.warn('moveViewsCardsToTrash: No cards available for move');
        return;
      }
      const body = { boardId, cardIds, columnType: 'TRASH' };
      requestApi(`/card/cards/move`, { excludeErrors: [400, 403, 404], method: 'POST', body });
    });
    showSnackBar({ message: i18next.t('board-card:trashToast.message'), actionButton: { caption: i18next.t('buttons.undo'), callback: () => { store.dispatch(actions.restoreViewsCards(boardWiseCardIds)) } } });
  } catch (error) {
    console.error('Views error while moving cards to Trash', error);
  }
}

/**
 * Restores given cards to it's previous column.
 * @param {Object} boardWiseCardIds Board wise card ids. e.g {'brd_5jgpem59gldl': ['crd_4jnbmkfvjdk', ...], ...}
 */
function* restoreViewsCards({ boardWiseCardIds }) {
  try {
    forEach(boardWiseCardIds, (cardIds) => {
      const body = { cardIds };
      requestApi(`/card/cards/restore`, { method: 'POST', body, excludeErrors: [400, 409] });
    });
  } catch (error) {
    console.error('Views error while restoring cards from Done or Trash column.', error);
  }
}

/**
 * @param {String} columnId Column Id of VIEW from which cards are being moved to done / trash.
 * @returns {Object} Boardwise card ids e.g. {$boardId: [$cardId1, $cardId2, ...], ...}
 */
function* getViewsCardsToBeMoved(columnId) {
  const state = yield select();
  const selectedCards = views.selectors.selectedCards(state);
  columnId = columnId || views.selectors.selectedColumn(state);

  let cards = selectedCards;
  if (isEmpty(cards) && columnId) {
    const cardsByColumn = get(views.selectors.cardsList({ state, columnId }), 'cards');
    cards = map(cardsByColumn, 'cardId');
  }


  // Returns if no cards found selected or in given column.
  if (isEmpty(cards)) {
    return;
  }


  // Filter out cards which have write permission on board.
  let boardWiseCardIds = {};
  forEach(cards, (cardId) => {
    const cardAttr = selectors.attrs(state, cardId);
    const boardId = get(cardAttr, 'boardId');
    const boardEffectivePemrission = board.selectors.boardEffectivePermission(state, boardId);
    if (boardEffectivePemrission && boardEffectivePemrission.hasWrite()) {
      if (!boardWiseCardIds[boardId]) {
        boardWiseCardIds[boardId] = [cardId];
      } else {
        boardWiseCardIds[boardId].push(cardId);
      }
    }
  })
  return boardWiseCardIds;
}

function* generateAiText({ cardId, prompt }) {
  if (!cardId || !prompt) {
    console.error('Generate AI Text: Card Id or prompt is not provided.', { cardId, prompt });
    return;
  }

  const body = { prompt, context: {entityId: cardId, entityType: 'CARD'} };
  try {
    const response = yield call(requestApi, `/card/ai-assistant`, { method: 'POST', body, excludeErrors: [400, 403, 409] });
    yield put(actions.generateAiTextSuccess(cardId, response?.text || ''));
  } catch (error) {
    console.error('Failed to generate AI text for card.', error);
    yield put(actions.generateAiTextFailure(cardId, error && error.code || 'UNKNOWN'));
  }
}

function* aiAssistanceDialogOpened({ cardId }) {
  const state = yield select();
  cardId = cardId || router.selectors.cardId(state);
  yield put(actions.generateAiText(cardId, 'Analyze this card and provide a summary'));
}

/**
 * Init Saga.
 */
function* saga() {
  yield all([
    call(watchRouter),
    takeEvery(actions.LOAD_CARDS_BY_COLUMN_TYPE, loadCardsByColumnType),
    takeEvery(actions.DISCONNECT_CARDS_BY_COLUMN_TYPE, disconnectCardsByColumnType),
    takeEvery(actions.LOAD_BOARD_CARDS, loadBoardCards),
    takeEvery(actions.ADD_NEW_CARD, addNewCard),
    takeEvery(actions.MOVE_TO_DONE, moveToDone),
    takeEvery(actions.MOVE_TO_TRASH, moveToTrash),
    takeEvery(actions.RESTORE, restore),
    takeEvery(actions.RESTORE_ALL, restoreAll),
    takeEvery(actions.UNDO_RESTORE, undoRestore),
    takeEvery(actions.GET_LINK, getLink),
    takeEvery(actions.COPY_AS_MARKDOWN, copyAsMarkdown),
    takeEvery(actions.GET_LINKS, getLinks),
    takeEvery(actions.COPY_CARDS_AS_MARKDOWN, copyCardsAsMarkdown),
    takeEvery(actions.COPY, copyCards),
    takeEvery(actions.PERMANENT_DELETE, permanentDelete),
    takeEvery(actions.SORT, sortCards),
    takeEvery(actions.UNDO_SORT, undoSort),
    takeEvery(actions.MOVE_CARDS_TO_TOP, moveSelectedCardsToTop),
    takeEvery(actions.MOVE_CARDS_TO_BOTTOM, moveSelectedCardsToBottom),
    takeEvery(actions.MOVE_CARDS_TO_DONE, moveCardsToDone),
    takeEvery(actions.MOVE_CARDS_TO_TRASH, moveCardsToTrash),
    takeEvery(actions.UNDO_MOVE, undoMove),
    takeEvery(actions.MOVE_CARDS_TO_ANOTHER_COLUMN, moveCardsToAnotherColumn),
    takeEvery(actions.MOVE_CARDS_ACROSS_BOARD, moveCardsToAnotherBoard),
    takeEvery(actions.MOVE_CARDS_WITHIN_BOARD_BEFORE_CARD, moveCardsWithinBoardBeforeCard),
    takeEvery(actions.MOVE_VIEWS_CARDS_TO_DONE, moveViewsCardsToDone),
    takeEvery(actions.MOVE_VIEWS_CARDS_TO_TRASH, moveViewsCardsToTrash),
    takeEvery(actions.RESTORE_VIEWS_CARDS, restoreViewsCards),
    takeEvery(actions.GENERATE_AI_TEXT, generateAiText),
    takeEvery(actions.AI_ASSISTANCE_DIALOG_OPENED, aiAssistanceDialogOpened)
  ]);
}

  export default saga;